import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["questionContainer", "previousButton", "nextButton", "progressBar", "progressText"]
  static values = { jobListingId: Number, totalQuestions: Number }

  connect() {
    this.answers = new Map()
    this.isProcessing = false
    this.updateButtons()
  }

  selectAnswer(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isProcessing) return

    const button = event.currentTarget
    const score = parseInt(button.dataset.score)
    const questionId = parseInt(button.dataset.questionId)

    if (!score || !questionId) return

    // Remove selection from other options
    this.element.querySelectorAll('.score-option').forEach(option => {
      option.classList.remove('selected', 'border-orange-500', 'bg-orange-100')
      option.classList.add('border-gray-300', 'bg-white')
    })

    // Add selection to clicked option
    button.classList.add('selected', 'border-orange-500', 'bg-orange-100')
    button.classList.remove('border-gray-300', 'bg-white')

    // Store answer
    this.currentQuestionId = questionId
    this.answers.set(questionId, score)
    
    // Add visual feedback
    button.classList.add('animate-pulse')
    this.updateNextButton()

    // Auto-advance after short delay
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.nextQuestion()
      }, 300)
    })
  }

  nextQuestion() {
    if (this.isProcessing) return

    const currentAnswer = this.answers.get(this.currentQuestionId)
    if (!currentAnswer) return

    this.isProcessing = true

    try {
      const url = `/job_listings/${this.jobListingIdValue}/position_assessment/next_question`
      const body = new FormData()
      body.append('question_id', this.currentQuestionId)
      body.append('score', currentAnswer)

      fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/html'
        },
        body: body
      })
      .then(response => {
        if (response.redirected) {
          window.location.href = response.url
          return
        }
        
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`HTTP ${response.status}: ${text}`)
          })
        }
        
        return response.text()
      })
      .then(html => {
        if (html) {
          this.questionContainerTarget.innerHTML = html
          this.updateButtons()
          this.updateProgress()
        }
      })
      .catch(error => {
        console.error('Error advancing to next question:', error)
        alert(`Error advancing to next question: ${error.message}`)
      })
      .finally(() => {
        this.isProcessing = false
      })
    } catch (error) {
      console.error('Error in nextQuestion:', error)
      this.isProcessing = false
    }
  }

  previousQuestion() {
    if (this.isProcessing) return

    this.isProcessing = true

    try {
      const url = `/job_listings/${this.jobListingIdValue}/position_assessment/previous_question`

      fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/html'
        }
      })
      .then(response => response.text())
      .then(html => {
        this.questionContainerTarget.innerHTML = html
        this.updateButtons()
        this.updateProgress()
      })
      .catch(error => {
        console.error('Error going to previous question:', error)
        alert('Error going to previous question. Please try again.')
      })
      .finally(() => {
        this.isProcessing = false
      })
    } catch (error) {
      console.error('Error in previousQuestion:', error)
      this.isProcessing = false
    }
  }

  updateButtons() {
    // Previous button is always enabled (controller will handle first question)
    if (this.hasPreviousButtonTarget) {
      this.previousButtonTarget.disabled = false
    }

    this.updateNextButton()
  }

  updateNextButton() {
    if (this.hasNextButtonTarget) {
      const hasSelection = this.element.querySelector('.score-option.selected')
      this.nextButtonTarget.disabled = !hasSelection
    }
  }

  updateProgress() {
    const answeredQuestions = this.answers.size
    const totalQuestions = this.totalQuestionsValue
    const progressPercentage = (answeredQuestions / totalQuestions) * 100

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progressPercentage}%`
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${Math.round(progressPercentage)}%`
    }
  }
}
